import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './display.css';

export type ToastTone = 'neutral' | 'success' | 'danger' | 'warning' | 'info';

export interface ToastOptions {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** ms; 0 = sticky. Default 4500. */
  duration?: number;
  action?: { label: string; onClick: () => void };
  id?: string;
}

interface ToastEntry extends ToastOptions {
  id: string;
  createdAt: number;
}

let toasts: ToastEntry[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
let seq = 0;

function push(options: ToastOptions): string {
  const id = options.id ?? `t${++seq}`;
  toasts = [...toasts.filter((t) => t.id !== id), { ...options, id, createdAt: Date.now() }].slice(-5);
  emit();
  return id;
}

export function dismissToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

type ToastFn = ((options: ToastOptions | string) => string) & {
  success(title: ReactNode, rest?: Omit<ToastOptions, 'title' | 'tone'>): string;
  error(title: ReactNode, rest?: Omit<ToastOptions, 'title' | 'tone'>): string;
  info(title: ReactNode, rest?: Omit<ToastOptions, 'title' | 'tone'>): string;
  warning(title: ReactNode, rest?: Omit<ToastOptions, 'title' | 'tone'>): string;
  dismiss: typeof dismissToast;
};

/** Show a toast from anywhere (no hook needed). */
export const toast: ToastFn = Object.assign((o: ToastOptions | string) => push(typeof o === 'string' ? { title: o } : o), {
  success: (title: ReactNode, rest: Omit<ToastOptions, 'title' | 'tone'> = {}) => push({ ...rest, title, tone: 'success' }),
  error: (title: ReactNode, rest: Omit<ToastOptions, 'title' | 'tone'> = {}) => push({ ...rest, title, tone: 'danger' }),
  info: (title: ReactNode, rest: Omit<ToastOptions, 'title' | 'tone'> = {}) => push({ ...rest, title, tone: 'info' }),
  warning: (title: ReactNode, rest: Omit<ToastOptions, 'title' | 'tone'> = {}) => push({ ...rest, title, tone: 'warning' }),
  dismiss: dismissToast,
});

const ICONS: Record<ToastTone, ReactNode> = {
  neutral: null,
  success: <CircleCheck size={16} />,
  danger: <CircleAlert size={16} />,
  warning: <TriangleAlert size={16} />,
  info: <Info size={16} />,
};

function ToastView({ t }: { t: ToastEntry }) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const duration = t.duration ?? 4500;
  const start = () => {
    if (duration > 0) timer.current = setTimeout(() => dismissToast(t.id), duration);
  };
  const stop = () => clearTimeout(timer.current);
  useEffect(() => {
    start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.id, t.createdAt]);
  const tone = t.tone ?? 'neutral';
  return (
    <div className="g-toast" data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'} onMouseEnter={stop} onMouseLeave={start}>
      {ICONS[tone] && <span className="g-toast__icon">{ICONS[tone]}</span>}
      <div className="g-toast__body">
        <div className="g-toast__title">{t.title}</div>
        {t.description && <div className="g-toast__desc">{t.description}</div>}
      </div>
      {t.action && (
        <button
          type="button"
          className="g-toast__action"
          onClick={() => {
            t.action!.onClick();
            dismissToast(t.id);
          }}
        >
          {t.action.label}
        </button>
      )}
      <button type="button" className="g-toast__close" aria-label="Dismiss" onClick={() => dismissToast(t.id)}>
        <X size={14} />
      </button>
    </div>
  );
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const snapshot = () => toasts;

/** Mount once near the app root. */
export function Toaster() {
  const list = useSyncExternalStore(subscribe, snapshot, snapshot);
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="g-toaster" aria-live="polite">
      {list.map((t) => (
        <ToastView key={t.id} t={t} />
      ))}
    </div>,
    document.body,
  );
}
